import { computed, onUnmounted, ref } from 'vue'
import { AUTH_OTP_RESEND_COOLDOWN_SECONDS } from '~/config/auth'

function formatCooldownSeconds(totalSeconds: number): string {
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

/**
 * Countdown for OTP resend actions. Starts automatically when `startCooldown` is called
 * (e.g. after OTP is sent or after a successful resend).
 */
export function useOtpResendCooldown(
    cooldownSeconds: number = AUTH_OTP_RESEND_COOLDOWN_SECONDS,
) {
    const remainingSeconds = ref(0)
    let intervalId: ReturnType<typeof setInterval> | undefined

    const canResend = computed(() => remainingSeconds.value === 0)

    const resendLabel = computed(() =>
        canResend.value
            ? 'Resend OTP'
            : `Resend OTP in ${formatCooldownSeconds(remainingSeconds.value)}`,
    )

    function clearTimer() {
        if (intervalId !== undefined) {
            clearInterval(intervalId)
            intervalId = undefined
        }
    }

    function startCooldown(fromSeconds: number = cooldownSeconds) {
        clearTimer()
        remainingSeconds.value = fromSeconds

        intervalId = setInterval(() => {
            if (remainingSeconds.value <= 1) {
                remainingSeconds.value = 0
                clearTimer()
                return
            }
            remainingSeconds.value -= 1
        }, 1000)
    }

    function resetCooldown() {
        clearTimer()
        remainingSeconds.value = 0
    }

    onUnmounted(clearTimer)

    return {
        canResend,
        remainingSeconds,
        resendLabel,
        startCooldown,
        resetCooldown,
    }
}
